import { Component } from 'react';
import sharedStyles from '../single-page.module.scss';
import { genStyles } from '../converter-layout/generator-layout';
import styles from './json-yaml-xml-converter.component.module.scss';

type Format = 'JSON' | 'YAML' | 'XML';

interface ConvState {
    input: string;
    output: string;
    inputFormat: Format;
    outputFormat: Format;
    error: string;
    info: string;
    copiedField: string | null;
}

const JSON_SAMPLE = `{
  "name": "John",
  "age": 30,
  "city": "New York"
}`;

const YAML_SAMPLE = `name: John
age: 30
city: New York`;

const XML_SAMPLE = `<root>
  <name>John</name>
  <age>30</age>
  <city>New York</city>
</root>`;

function sampleFor(format: Format): string {
    if (format === 'YAML') return YAML_SAMPLE;
    if (format === 'XML') return XML_SAMPLE;
    return JSON_SAMPLE;
}

async function parseInput(text: string, format: Format): Promise<any> {
    if (format === 'JSON') return JSON.parse(text);
    if (format === 'YAML') {
        const yaml = await import('js-yaml');
        try {
            return yaml.load(text);
        } catch (e: any) {
            const msg = e.message || '';
            const dupMatch = msg.match(/duplicated mapping key.*line (\d+)/is);
            if (dupMatch) {
                const line = parseInt(dupMatch[1], 10);
                const lines = text.split('\n');
                const key = lines[line - 1]?.match(/^\s*([^:\s]+)/)?.[1];
                throw new Error(key ? `Duplicate key "${key}" at line ${line}` : `Duplicate key at line ${line}`);
            }
            throw e;
        }
    }
    if (format === 'XML') {
        const { XMLParser } = await import('fast-xml-parser');
        return new XMLParser({ ignoreAttributes: false }).parse(text);
    }
}

async function serialize(obj: any, format: Format): Promise<string> {
    if (format === 'JSON') return JSON.stringify(obj, null, 2);
    if (format === 'YAML') {
        const yaml = await import('js-yaml');
        return yaml.dump(obj, { indent: 2, lineWidth: -1 });
    }
    if (format === 'XML') {
        const { XMLBuilder } = await import('fast-xml-parser');
        const builder = new XMLBuilder({ format: true, indentBy: '  ', ignoreAttributes: false });
        return builder.build({ root: obj });
    }
    return '';
}

function countKeys(obj: any): number {
    if (typeof obj !== 'object' || obj === null) return 0;
    return Object.keys(obj).length;
}

export default class JsonYamlXmlConverterComponent extends Component<{ feature?: string }, ConvState> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;

    constructor(props: { feature?: string }) {
        super(props);
        const { inputFormat, outputFormat } = this.deriveFormats(props.feature);
        this.state = {
            input: sampleFor(inputFormat), output: '', inputFormat, outputFormat,
            error: '', info: '', copiedField: null,
        };
    }

    private deriveFormats(feature?: string): { inputFormat: Format; outputFormat: Format } {
        const match = feature?.match(/convert-(\w+)-to-(\w+)/);
        if (match) {
            const from = match[1].toUpperCase() as Format;
            const to = match[2].toUpperCase() as Format;
            if (['JSON', 'YAML', 'XML'].includes(from) && ['JSON', 'YAML', 'XML'].includes(to)) {
                return { inputFormat: from, outputFormat: to };
            }
        }
        return { inputFormat: 'JSON', outputFormat: 'YAML' };
    }

    componentDidMount() { this.convert(this.state.input); }

    async convert(input: string) {
        if (!input.trim()) { this.setState({ output: '', error: '', info: '' }); return; }
        try {
            const obj = await parseInput(input, this.state.inputFormat);
            const output = await serialize(obj, this.state.outputFormat);
            const keys = countKeys(obj);
            this.setState({
                output: output.trim(), error: '',
                info: `✓ Valid ${this.state.inputFormat} (${keys} key${keys !== 1 ? 's' : ''}, ${new Blob([input]).size} bytes)`,
            });
        } catch (e: any) {
            const msg = e.message || 'Parse error';
            const lineMatch = msg.match(/line (\d+)/i) || msg.match(/position (\d+)/i);
            const detail = lineMatch ? ` at line ${lineMatch[1]}` : '';
            this.setState({ error: `${this.state.inputFormat} parse error${detail}: ${msg.split('\n')[0]}`, info: '' });
        }
    }

    handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const input = e.target.value;
        this.setState({ input });
        this.convert(input);
    };

    setInputFormat = (f: Format) => {
        this.setState({ inputFormat: f }, () => this.convert(this.state.input));
    };

    setOutputFormat = (f: Format) => {
        this.setState({ outputFormat: f }, () => this.convert(this.state.input));
    };

    swap = () => {
        this.setState(s => ({
            inputFormat: s.outputFormat, outputFormat: s.inputFormat,
            input: s.output, output: '', error: '', info: '',
        }), () => this.convert(this.state.input));
    };

    copyOutput = () => {
        navigator.clipboard.writeText(this.state.output).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copiedField: 'output' });
            this.copyTimeout = setTimeout(() => this.setState({ copiedField: null }), 2000);
        });
    };

    renderFormatSelect(value: Format, onChange: (f: Format) => void, label: string) {
        return (
            <div className={styles.formatSelect}>
                <label className={sharedStyles.label}>{label}</label>
                <select value={value} onChange={e => onChange(e.target.value as Format)} className={styles.select}>
                    <option value="JSON">JSON</option>
                    <option value="YAML">YAML</option>
                    <option value="XML">XML</option>
                </select>
            </div>
        );
    }

    render() {
        const { input, output, inputFormat, outputFormat, error, info, copiedField } = this.state;
        const isCopied = copiedField === 'output';

        return (
            <div className="row">
                <div className="col-12">
                    <div className={genStyles.section}>
                        <div className={styles.header}>
                            {this.renderFormatSelect(inputFormat, this.setInputFormat, 'Input')}
                            <button className={styles.swapButton} onClick={this.swap} title="Swap formats">⇄</button>
                            {this.renderFormatSelect(outputFormat, this.setOutputFormat, 'Output')}
                        </div>
                    </div>
                </div>

                <div className="col-12 col-md-6">
                    <div className={genStyles.section}>
                        <textarea className={sharedStyles.textarea} value={input} onChange={this.handleInput}
                            rows={12} placeholder={`Paste ${inputFormat} here...`}
                            spellCheck={false} style={{ fontFamily: "'SF Mono', 'Fira Code', Menlo, monospace", fontSize: '0.85rem' }} />
                    </div>
                </div>

                <div className="col-12 col-md-6">
                    <div className={genStyles.section}>
                        <div className={styles.readOnlyWrapper}>
                            <span className={styles.readOnlyBadge}>Read Only</span>
                            <textarea className={sharedStyles.textarea} value={output} readOnly rows={12}
                                placeholder="Output will appear here..."
                                style={{ fontFamily: "'SF Mono', 'Fira Code', Menlo, monospace", fontSize: '0.85rem', background: '#f1f5f9' }} />
                        </div>
                    </div>
                </div>

                <div className="col-12">
                    <div className={genStyles.section}>
                        {error && <div className={sharedStyles.errorMessage} aria-live="polite">⚠ {error}</div>}
                        {info && <div className={styles.info}>{info}</div>}
                        <div className={styles.footer}>
                            <button className={`${sharedStyles.copyButton} ${isCopied ? sharedStyles.copyButtonSuccess : ''}`}
                                onClick={this.copyOutput}>
                                {isCopied ? '✓ Copied' : 'Copy'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}
